import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_URL, User } from '@frontend-site/shared/data';
import { catchError, forkJoin, map, Observable, tap, throwError } from 'rxjs';
import { UsersStore } from './users.store';

@Injectable({ providedIn: 'root' })
export class UsersService {
    constructor(
        @Inject(API_URL) private readonly apiUrl: string,
        private readonly http: HttpClient,
        private readonly usersStore: UsersStore
    ) {}

    loadProjectUsers(projectId: number): Observable<[User[], User[], User[]]> {
        return this.getProjectUsers(projectId).pipe(
            tap(([users, inactiveUsers, disconnectedUsers]) => {
                this.usersStore.setUsers(users, inactiveUsers, disconnectedUsers);
            })
        );
    }

    loadCompanyUsers(companyId: number): Observable<[User[], User[]]> {
        return this.getCompanyUsers(companyId).pipe(
            tap(([users, inactiveUsers]) => {
                this.usersStore.setUsers(users, inactiveUsers, []);
            })
        );
    }

    private getProjectUsers(projectId: number): Observable<[User[], User[], User[]]> {
        return forkJoin([
            this.loadProjectActiveUsers(projectId),
            this.loadProjectInactiveUsers(projectId),
            this.loadProjectDisconnectedUsers(projectId)
        ]);
    }

    private getCompanyUsers(companyId: number): Observable<[User[], User[]]> {
        return forkJoin([this.loadCompanyUsersFromServer(companyId, true, false), this.loadCompanyUsersFromServer(companyId, false, true)]);
    }

    public updateUsers(projectId: number) {
        this.getProjectUsers(projectId)
            .pipe(tap(([users, inactiveUsers, disconnectedUsers]) => this.usersStore.setUsers(users, inactiveUsers, disconnectedUsers)))
            .subscribe();
    }

    private loadProjectActiveUsers(projectId: number): Observable<User[]> {
        const params = new HttpParams().set('projectId', projectId);
        return this.http.get<User[]>(`${this.apiUrl}/general/usersInProject`, { params }).pipe(
            catchError((err) => {
                console.log('failed loading users per project', err);
                return throwError(() => err);
            })
        );
    }

    private loadProjectInactiveUsers(projectId: number): Observable<User[]> {
        return this.http.get<any>(`${this.apiUrl}/UM/inactive`, { params: { projectId } }).pipe(
            map((users) => UsersService.convertUsers(users?.result)),
            catchError((err) => {
                console.log('failed loading inactive users per project', err);
                return throwError(() => err);
            })
        );
    }

    private loadProjectDisconnectedUsers(projectId: number): Observable<User[]> {
        return this.http.get<any>(`${this.apiUrl}/UM/disconnected`, { params: { projectId } }).pipe(
            map((users) => UsersService.convertUsers(users?.result)),
            catchError((err) => {
                console.log('failed loading disconnected users per project', err);
                return throwError(() => err);
            })
        );
    }

    private static convertUsers(users: any): User[] {
        if (!users) {
            return users;
        }
        return users.map((user: { firstName: string; lastName: string }) => {
            return {
                ...user,
                fullName: user.firstName + ' ' + user.lastName,
                name: user.firstName + ' ' + user.lastName
            };
        });
    }

    private loadCompanyUsersFromServer(companyId: number, includeActive: boolean, includeInactive: boolean): Observable<User[]> {
        return this.http
            .get<User[]>('/rest/general/searchUsers', {
                params: {
                    companyId: companyId.toString(),
                    includeActive,
                    includeInactive,
                    limitResults: false
                }
            })
            .pipe(
                catchError((err) => {
                    console.log('failed loading users per company', err);
                    return throwError(() => err);
                })
            );
    }
}
